import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import ZoomPan from "@/components/ZoomPan";
import TitleBlock from "@/components/TitleBlock";
import PlanActions from "@/components/PlanActions";
import FacadeBadges from "@/components/FacadeBadges";
import { Card, Button } from "@/components/ui";

// Dimension line with end ticks + centred label (extracted to module scope
// so it is a stable component, not redefined every render).
const Dim = ({ x1, y1, x2, y2, label, vertical }) => (
  <g>
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0F172A" strokeWidth={1} />
    <line x1={x1} y1={vertical ? y1 : y1 - 4} x2={x1} y2={vertical ? y1 : y1 + 4} {...(vertical ? { x2: x1 + 4, x1: x1 - 4 } : {})} stroke="#0F172A" strokeWidth={1} />
    <line x1={x2} y1={vertical ? y2 : y2 - 4} x2={x2} y2={vertical ? y2 : y2 + 4} {...(vertical ? { x2: x2 + 4, x1: x2 - 4 } : {})} stroke="#0F172A" strokeWidth={1} />
    <text x={vertical ? x1 - 6 : (x1 + x2) / 2} y={vertical ? (y1 + y2) / 2 : y1 + 13}
          fontSize={11} fontWeight="800" fill="#0F172A" textAnchor="middle"
          style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}
          transform={vertical ? `rotate(-90, ${x1 - 6}, ${(y1 + y2) / 2})` : undefined}>
      {label}
    </text>
  </g>
);

function FoundationPlan({ data, walls = [], facadeMeta = {}, designCode, w = 900, h = 680 }) {
  const geo = useMemo(() => {
    const pts = [];
    (data.footings || []).forEach((f) => { pts.push([f.cx - f.w / 2, f.cy - f.h / 2], [f.cx + f.w / 2, f.cy + f.h / 2]); });
    (data.columns || []).forEach((c) => { pts.push([c.x, c.y]); });
    (data.grid?.x_grid || []).forEach((g) => { pts.push([g.x1, g.y], [g.x2, g.y]); });
    (data.grid?.y_grid || []).forEach((g) => { pts.push([g.x, g.y1], [g.x, g.y2]); });
    (walls || []).forEach((wl) => { pts.push([wl.p1.x, wl.p1.y], [wl.p2.x, wl.p2.y]); });
    if (!pts.length) return null;
    const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
    const xmin = Math.min(...xs), xmax = Math.max(...xs), ymin = Math.min(...ys), ymax = Math.max(...ys);
    const W = xmax - xmin || 1, H = ymax - ymin || 1, pad = 82;
    const s = Math.min((w - pad * 2) / W, (h - pad * 2) / H);
    return { px: (x) => (x - xmin) * s + (w - W * s) / 2, py: (y) => (y - ymin) * s + (h - H * s) / 2, s, xmin, xmax, ymin, ymax };
  }, [data, walls, w, h]);

  if (!geo) return null;
  const { px, py, s, xmin, xmax, ymin, ymax } = geo;
  const isAr = document?.dir === "rtl" || (typeof navigator !== "undefined" && (navigator.language || "").startsWith("ar"));
  // Grid axis positions → bay (span) dimensions between consecutive axes.
  const xAxes = [...new Set((data.grid?.x_labels || []).map((l) => l.x))].sort((a, b) => a - b);
  const yAxes = [...new Set((data.grid?.y_labels || []).map((l) => l.y))].sort((a, b) => a - b);
  const yDim = py(ymax) + 34;   // frontage chain below the plan
  const xDim = px(xmin) - 34;   // depth chain to the left
  const seg = (aList) => {
    const out = [];
    for (let i = 0; i < aList.length - 1; i++) out.push([aList[i], aList[i + 1]]);
    return out;
  };
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ background: "#fff" }} xmlns="http://www.w3.org/2000/svg">
      {/* faint room partitions (from the architectural model) so it is visible
          that the columns sit ON the partition/axis lines - structure follows
          the plan. Drawn first → sits behind footings & columns. */}
      {(walls || []).map((wl, i) => (
        <line key={`wl-${i}`} x1={px(wl.p1.x)} y1={py(wl.p1.y)} x2={px(wl.p2.x)} y2={py(wl.p2.y)}
              stroke={wl.wall_type === "exterior" ? "#CBD5E1" : "#E2E8F0"}
              strokeWidth={wl.wall_type === "exterior" ? 3 : 1.5} strokeLinecap="round" />
      ))}
      {(data.grid?.x_grid || []).map((g, i) => <line key={`gx-${i}`} x1={px(g.x1)} y1={py(g.y)} x2={px(g.x2)} y2={py(g.y)} stroke="#94A3B8" strokeWidth={0.8} strokeDasharray="6 4" />)}
      {(data.grid?.y_grid || []).map((g, i) => <line key={`gy-${i}`} x1={px(g.x)} y1={py(g.y1)} x2={px(g.x)} y2={py(g.y2)} stroke="#94A3B8" strokeWidth={0.8} strokeDasharray="6 4" />)}
      {(data.grid?.x_labels || []).map((l, i) => <text key={`lx-${i}`} x={px(l.x)} y={20} fontSize={13} fontWeight="700" fill="#0EA5E9" textAnchor="middle">{l.label}</text>)}
      {(data.grid?.y_labels || []).map((l, i) => <text key={`ly-${i}`} x={16} y={py(l.y) + 4} fontSize={13} fontWeight="700" fill="#0EA5E9" textAnchor="middle">{l.label}</text>)}
      {(data.footings || []).map((f, i) => (
        <rect key={`f-${i}`} x={px(f.cx - f.w / 2)} y={py(f.cy - f.h / 2)} width={f.w * s} height={f.h * s} fill="#E2E8F0" stroke="#475569" strokeWidth={1.2} />
      ))}
      {/* per-footing dimension labels (W×H in metres) */}
      {(data.footings || []).map((f, i) => (f.w * s > 26 ? (
        <text key={`fl-${i}`} x={px(f.cx)} y={py(f.cy) + 4} fontSize={11} fontWeight="800" fill="#0F172A" textAnchor="middle" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 }}>
          {f.w.toFixed(1)}×{f.h.toFixed(1)}{f.type ? ` (${f.type})` : ""}
        </text>
      ) : null))}
      {(data.columns || []).map((c, i) => (
        <rect key={`c-${i}`} x={px(c.x) - (c.w * s) / 2} y={py(c.y) - (c.h * s) / 2} width={Math.max(5, c.w * s)} height={Math.max(5, c.h * s)} fill="#0F172A" stroke="#0EA5E9" strokeWidth={1} />
      ))}
      {/* ── dimension chains: grid-bay spans + overall frontage (bottom) & depth (left) ── */}
      {seg(xAxes).map(([a, b], i) => (
        <Dim key={`bx-${i}`} x1={px(a)} y1={yDim} x2={px(b)} y2={yDim} label={(b - a).toFixed(2)} />
      ))}
      <Dim x1={px(xmin)} y1={yDim + 18} x2={px(xmax)} y2={yDim + 18} label={`${isAr ? "الواجهة" : "Frontage"} ${(xmax - xmin).toFixed(2)} م`} />
      {seg(yAxes).map(([a, b], i) => (
        <Dim key={`by-${i}`} x1={xDim} y1={py(a)} x2={xDim} y2={py(b)} vertical label={(b - a).toFixed(2)} />
      ))}
      <Dim x1={xDim - 18} y1={py(ymin)} x2={xDim - 18} y2={py(ymax)} vertical label={`${isAr ? "العمق" : "Depth"} ${(ymax - ymin).toFixed(2)} م`} />

      {/* Facade context badges - shared component (see FacadeBadges.jsx).
          Foundation.jsx renders unflipped: small model-y = TOP of screen
          (opposite to PlanSVG). So the "front" (small y) badge is drawn
          at the TOP. */}
      <FacadeBadges
        meta={facadeMeta}
        designCode={designCode}
        positions={{
          front: { cx: px((xmin + xmax) / 2), cy: py(ymin) - 22 },
          rear:  { cx: px((xmin + xmax) / 2), cy: py(ymax) + 22 },
          left:  { cx: px(xmin) - 22,          cy: py((ymin + ymax) / 2) },
          right: { cx: px(xmax) + 22,          cy: py((ymin + ymax) / 2) },
        }}
      />
    </svg>
  );
}

export default function Foundation() {
  const { lang, user } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();
  const [floors, setFloors] = useState(wm?.floors || 1);
  const [buildingKind, setBuildingKind] = useState(wm?.kind || "villa");
  const [soilBearing, setSoilBearing] = useState(150);
  const [slabSystem, setSlabSystem] = useState(wm?.slab_system || "solid");
  const [showTB, setShowTB] = useState(true);

  const { data, loading, error } = useDeliverable(
    () => api.editorSmartFoundation({ model: wm.model, floors, building_kind: buildingKind, slab_system: slabSystem, soil_bearing: soilBearing, design_code: wm.design_code }),
    [floors, buildingKind, soilBearing, slabSystem, wm?.title, wm?.design_code],
    !!wm?.model
  );
  const sum = data?.summary || {};

  const { data: sched } = useDeliverable(
    () => api.editorStructuralSchedule({ model: wm.model, kind: buildingKind, floors, slab_system: slabSystem, design_code: wm.design_code }),
    [floors, buildingKind, slabSystem, wm?.title, wm?.design_code],
    !!wm?.model
  );

  const kinds = [["villa", isAr ? "فيلا" : "Villa"], ["apartment", isAr ? "شقة" : "Apt"], ["office", isAr ? "مكتب" : "Office"], ["hotel", isAr ? "فندق" : "Hotel"], ["mall", isAr ? "تجاري" : "Mall"]];

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <PageHeader
            eyebrow={isAr ? "الأساسات" : "Foundation"}
            title={isAr ? "مخطط الأساسات والأعمدة" : "Foundation & column grid"}
            subtitle={isAr ? "قواعد منفصلة بمقاسات محسوبة حسب حمل كل عمود + شبكة المحاور." : "Isolated footings sized per column load + structural axis grid."}
            right={<Button size="sm" variant="outline" onClick={() => api.editorDownloadSheet("foundation", wm.model, wm.title, { floors, slab_system: slabSystem }).catch((e) => toast.error(String(e.message || e)))} data-testid="foundation-sheet"><FileText size={15} /> {isAr ? "لوحة الأساسات PDF" : "Foundation PDF"}</Button>}
          />
          <PlanActions
            model={wm.model}
            projectName={wm.title}
            sheet="foundation"
            sheetMeta={{ floors, slab_system: slabSystem }}
            testIdPrefix="foundation-actions"
          />
          {/* per-plan edit toolbar - live recompute */}
          <Card className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 p-3" data-testid="foundation-toolbar">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "طوابق:" : "Floors:"}</span>
              {[1, 2, 3, 4, 5].map((f) => (
                <button key={f} data-testid={`fnd-floors-${f}`} onClick={() => setFloors(f)}
                  className={`rounded-full border px-3 py-1 text-xs font-bold ${floors === f ? "border-sky-500 bg-sky-500 text-white" : "border-border bg-card text-foreground hover:border-sky-400"}`}>{f}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "نوع البناء:" : "Building:"}</span>
              {kinds.map(([k, l]) => (
                <button key={k} data-testid={`fnd-kind-${k}`} onClick={() => setBuildingKind(k)}
                  className={`rounded-full border px-3 py-1 text-xs font-bold ${buildingKind === k ? "border-emerald-500 bg-emerald-500 text-white" : "border-border bg-card text-foreground hover:border-emerald-400"}`}>{l}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "قدرة التربة:" : "Soil:"}</span>
              {[100, 150, 200, 250, 300].map((s) => (
                <button key={s} data-testid={`fnd-soil-${s}`} onClick={() => setSoilBearing(s)}
                  className={`rounded-full border px-3 py-1 text-xs font-bold ${soilBearing === s ? "border-amber-500 bg-amber-500 text-white" : "border-border bg-card text-foreground hover:border-amber-400"}`}>{s}</button>
              ))}
              <span className="text-[10px] text-muted-foreground">kN/m²</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "بلوك العنوان:" : "Title block:"}</span>
              <button data-testid="toggle-title-block" onClick={() => setShowTB((v) => !v)}
                className={`rounded-full border px-3 py-1 text-xs font-bold ${showTB ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:border-primary/40"}`}>{showTB ? (isAr ? "ظاهر" : "On") : (isAr ? "مخفي" : "Off")}</button>
            </div>
          </Card>
          <DeliverableState loading={loading} error={error} isAr={isAr}>
            {data && (
              <>
                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    [isAr ? "عدد الأعمدة" : "Columns", sum.column_count],
                    [isAr ? "أقصى بحر" : "Max span", `${sum.max_span_m} m`],
                    [isAr ? "خرسانة الأعمدة" : "Column concrete", `${sum.total_concrete_m3_columns} m³`],
                    [isAr ? "خرسانة القواعد" : "Footing concrete", `${sum.total_concrete_m3_footings} m³`],
                  ].map(([l, v]) => (
                    <Card key={l} className="p-4"><div className="text-xl font-black">{v ?? "—"}</div><div className="text-xs text-muted-foreground">{l}</div></Card>
                  ))}
                </div>
                <Card className="p-3">
                  <ZoomPan className="h-[560px]"><div className="min-w-[700px] p-4"><FoundationPlan data={data} walls={wm?.model?.walls} facadeMeta={wm?.model?.facade_meta} designCode={wm?.design_code} /></div></ZoomPan>
                </Card>

                {sched?.column_schedule?.length > 0 && (
                  <Card className="mt-4 p-4" data-testid="structural-schedule">
                    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-sm font-black">{isAr ? "جدول الأعمدة والكمرات - تصميم مبدئي حسب الأحمال" : "Column & beam schedule - load-based preliminary design"}</h3>
                      <span className="text-[11px] font-bold text-sky-500">{sched.code}</span>
                    </div>
                    <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                      <span>{isAr ? "الأحمال المضاعفة" : "Factored"} wu = <b className="text-foreground">{sched.loads.factored_wu_kn_m2} kN/m²</b> ({sched.loads.combination})</span>
                      <span>{isAr ? "حي" : "LL"} {sched.loads.live_load_kn_m2} · {isAr ? "بلاطة" : "slab"} {sched.loads.slab_self_weight_kn_m2} · {isAr ? "ميت إضافي" : "SDL"} {sched.loads.super_dead_kn_m2} kN/m²</span>
                      <span>{sched.materials.concrete_grade} · {sched.materials.steel_grade}</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[560px] border-collapse text-start text-xs">
                        <thead>
                          <tr className="border-b-2 border-slate-300 text-[10px] uppercase text-muted-foreground">
                            {[isAr ? "الرمز" : "Mark", isAr ? "المقطع (مم)" : "Section (mm)", isAr ? "التسليح الطولي" : "Long. bars", isAr ? "الكانات" : "Ties", "ρ%", isAr ? "العدد" : "Qty", isAr ? "أقصى حِمل" : "Max Pu"].map((h) => (
                              <th key={h} className="px-2 py-1.5 text-start font-black">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sched.column_schedule.map((g) => (
                            <tr key={g.mark} className="border-b border-slate-200" data-testid={`col-row-${g.mark}`}>
                              <td className="px-2 py-1.5 font-black text-sky-600">{g.mark}</td>
                              <td className="px-2 py-1.5 font-bold">{g.size}</td>
                              <td className="px-2 py-1.5">{g.long_bars}</td>
                              <td className="px-2 py-1.5">{g.ties}</td>
                              <td className="px-2 py-1.5">{g.rho_pct}</td>
                              <td className="px-2 py-1.5">×{g.count}</td>
                              <td className="px-2 py-1.5 text-muted-foreground">≤ {g.max_pu_kn} kN</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {sched.beam_schedule?.length > 0 && (
                      <div className="mt-4 overflow-x-auto">
                        <p className="mb-1 text-xs font-black">{isAr ? "الكمرات الرئيسية" : "Primary beams"}</p>
                        <table className="w-full min-w-[560px] border-collapse text-start text-xs">
                          <thead>
                            <tr className="border-b-2 border-slate-300 text-[10px] uppercase text-muted-foreground">
                              {[isAr ? "الرمز" : "Mark", isAr ? "البحر" : "Span", isAr ? "المقطع (مم)" : "Section (mm)", isAr ? "سفلي" : "Bottom", isAr ? "علوي" : "Top", isAr ? "الكانات" : "Stirrups"].map((h) => (
                                <th key={h} className="px-2 py-1.5 text-start font-black">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sched.beam_schedule.map((b) => (
                              <tr key={b.mark} className="border-b border-slate-200" data-testid={`beam-row-${b.mark}`}>
                                <td className="px-2 py-1.5 font-black text-emerald-600">{b.mark}</td>
                                <td className="px-2 py-1.5">{b.span_m} m</td>
                                <td className="px-2 py-1.5 font-bold">{b.size_mm}</td>
                                <td className="px-2 py-1.5">{b.bottom_bars}</td>
                                <td className="px-2 py-1.5">{b.top_bars}</td>
                                <td className="px-2 py-1.5 text-[11px]">{b.stirrups}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">{isAr ? sched.note_ar : sched.note_en}</p>
                  </Card>
                )}

                {showTB && (
                  <TitleBlock isAr={isAr} projectName={wm?.title} sheetTitle={isAr ? "مخطط الأساسات والأعمدة" : "Foundation & column plan"} sheetNo="S-02" scale="1:100" designCode={wm?.design_code} firm={user?.firm_profile} />
                )}
              </>
            )}
          </DeliverableState>
        </div>
      )}
    </RequireModel>
  );
}
